<template>
  <v-dialog :modelValue="show" max-width="600" persistent @update:modelValue="$emit('update:show', $event)">
    <v-card style="border-radius: 8px">
      <v-card-title
        style="background-color: #fec627; color: black; font-weight: bold; padding: 20px; position: relative"
      >
        <v-icon @click="closeModal" style="position: absolute; right: 16px; top: 16px; cursor: pointer; color: black"
          >mdi-close</v-icon
        >
        <div style="display: flex; align-items: center; gap: 12px">
          <span style="font-size: 24px">Session-based course recommended</span>
        </div>
      </v-card-title>

      <v-card-text style="padding: 24px; font-size: 16px; line-height: 1.5; text-align: left">
        You are enrolling in the on-demand version of <strong>{{ coursePrefix }} : {{ courseName }}</strong
        >. Learners have a higher completion rate in session-based courses.
      </v-card-text>

      <div style="padding: 0 24px; text-align: left; font-size: 16px; margin-bottom: 24px">
        Choose how you would like to take this course:
      </div>

      <div style="padding: 0 24px; display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px">
        <!-- Session-based option card -->
        <v-card
          @click="selectSessionBased"
          :style="
            selectedOption === 'session'
              ? 'border: 1px solid #00A3E0; background-color: #00A3E00D;'
              : 'border: 1px solid #ccc; background-color: white;'
          "
          style="cursor: pointer; border-radius: 8px"
          hover
          :ripple="false"
        >
          <v-card-text style="padding: 24px; height: 100%">
            <div style="display: flex; align-items: flex-start">
              <div :class="selectedOption === 'session' ? 'checkbox-selected' : 'checkbox-unselected'">
                <v-icon v-if="selectedOption === 'session'" style="font-size: 12px; color: white">mdi-check</v-icon>
              </div>
              <div>
                <div style="color: #484848; font-size: 16px; margin-bottom: 8px">
                  <strong>Switch</strong> to <strong>session-based</strong> course
                </div>
                <ul style="margin: 0; padding-left: 16px; font-size: 16px; line-height: 24px">
                  <li><strong>Start:</strong> {{ sessionStartDate }}, <strong>End:</strong> {{ sessionEndDate }}</li>
                  <li>Due dates to keep you on track</li>
                  <li>Opportunities to learn with peers</li>
                </ul>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- On-demand option card -->
        <v-card
          @click="selectOnDemand"
          :style="
            selectedOption === 'ondemand'
              ? 'border: 1px solid #00A3E0; background-color: #00A3E00D;'
              : 'border: 1px solid #ccc; background-color: white;'
          "
          style="cursor: pointer; border-radius: 8px"
          hover
          :ripple="false"
        >
          <v-card-text style="padding: 24px; height: 100%">
            <div style="display: flex; align-items: flex-start">
              <div :class="selectedOption === 'ondemand' ? 'checkbox-selected' : 'checkbox-unselected'">
                <v-icon v-if="selectedOption === 'ondemand'" style="font-size: 12px; color: white">mdi-check</v-icon>
              </div>
              <div>
                <div style="color: #484848; font-size: 16px; margin-bottom: 8px">
                  <strong>Continue</strong> with <strong>on-demand</strong> course
                </div>
                <ul style="margin: 0; padding-left: 16px; font-size: 16px; line-height: 24px">
                  <li>Minimum six week enrollment period</li>
                  <li>No due dates</li>
                  <li>One year to complete the course</li>
                </ul>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Confirm Button -->
      <div style="padding: 0 24px 24px 24px; text-align: center">
        <v-btn @click="proceedWithSelection" :disabled="!selectedOption" class="continue-btn"> Confirm </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
import { getAttributionParams } from '@/util/param-persistence';

export default defineComponent({
  emits: ['update:show', 'close', 'enroll-in-session', 'continue-on-demand'],
  name: 'OnDemandWarningModal',
  mixins: [DataLayerMixin],

  props: {
    show: {
      type: Boolean,
      default: false
    },
    courseName: {
      type: String,
      required: true
    },
    coursePrefix: {
      type: String,
      default: ''
    },
    sessionStartDate: {
      type: String,
      required: true
    },
    sessionEndDate: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      selectedOption: null
    };
  },

  methods: {
    closeModal() {
      // Track close button click
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'modal',
          action: 'close',
          name: 'onclick',
          type: 'click',
          region: 'main content',
          section: 'session-based course recommended',
          text: 'close cross'
        });
      }
      this.$emit('close');
    },
    selectSessionBased() {
      this.selectedOption = 'session';
      // Track session-based selection
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'select',
          action: 'click',
          name: 'onclick',
          type: 'checkbox',
          region: 'main content',
          section: 'switch to session-based course',
          text: 'session-based course recommended'
        });
      }
    },
    selectOnDemand() {
      this.selectedOption = 'ondemand';
      // Track on-demand selection
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'select',
          action: 'click',
          name: 'onclick',
          type: 'checkbox',
          region: 'main content',
          section: 'session-based course recommended',
          text: 'continue with on-demand course'
        });
      }
    },
    proceedWithSelection() {
      if (this.selectedOption === 'session') {
        // Track confirm for session-based
        this.trackConfirmButton('session');
        this.$emit('enroll-in-session');
      } else if (this.selectedOption === 'ondemand') {
        // Track confirm for on-demand with ecommerce data
        this.trackConfirmButton('ondemand');
        this.$emit('continue-on-demand');
      }
    },
    trackConfirmButton(type) {
      if (window.dataLayer) {
        // Add ecommerce tracking for on-demand selection
        if (type === 'ondemand') {
          window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object

          const courseFullName = `${this.coursePrefix} ${this.courseName} on demand`.toLowerCase();
          const { campaign, partner } = getAttributionParams();
          window.dataLayer.push({
            event: 'add_to_cart',
            campaign,
            partner,
            ecommerce: {
              currency: 'USD',
              value: 25.0,
              items: [
                {
                  item_id: courseFullName,
                  item_name: courseFullName,
                  price: 25.0,
                  quantity: 1
                }
              ]
            }
          });
        }

        // Track modal close for confirm button
        window.dataLayer.push({
          event: 'modal',
          action: 'close',
          name: 'onclick',
          type: 'click',
          region: 'main content',
          section: 'session-based course recommended',
          text: 'confirm'
        });
      }
    }
  }
});
</script>

<style scoped>
.v-card__title {
  word-break: normal;
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Hide scrollbar by default, show only when scrolling */
.v-dialog {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.v-dialog::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.v-dialog:hover {
  scrollbar-width: thin; /* Firefox */
  -ms-overflow-style: auto; /* IE and Edge */
}

.v-dialog:hover::-webkit-scrollbar {
  display: block; /* Chrome, Safari, Opera */
  width: 8px;
}

.v-dialog:hover::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.checkbox-selected {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border: 1px solid #000000;
  border-radius: 50%;
  margin-right: 12px;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000000;
  flex-shrink: 0;
}

.checkbox-unselected {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border: 2px solid #bfbfbf;
  border-radius: 50%;
  margin-right: 12px;
  margin-top: 4px;
  margin-left: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  flex-shrink: 0;
}

.continue-btn {
  width: 352px !important;
  height: 48px !important;
  border-radius: 100px !important;
  padding: 12px 30px !important;
  background-color: #ffc627 !important;
  color: #484848 !important;
  font-family: Arial !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  text-transform: none !important;
  box-shadow: none !important;
}

@media (max-width: 600px) {
  .v-dialog {
    margin: 16px;
  }

  .continue-btn {
    width: 90% !important;
    max-width: 352px !important;
  }

  .checkbox-selected,
  .checkbox-unselected {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
  }

  div[style*='display: flex; gap: 16px'] {
    flex-direction: column !important;
  }
}
</style>
